import { memo, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import { InternalNotes as InternalNote } from "../../types/InternalNotes";

const SOURCE_LABEL: Record<string, string> = {
  internal: "Internal",
  allocation: "Allocation",
  redesign: "Redesign",
};

const SYSTEM_NOTE_EXACT = new Set([
  "Case allocated",
  "No note provided",
  "No reason provided",
]);

const SYSTEM_NOTE_PREFIXES = [
  "Designed check with job card",
  "Units marked",
  "Categories:",
  "No note provided for edit",
];

const UNITS_MARKED_LABELS = [
  "Design TN",
  "Design units",
  "Delivery TN",
  "Delivery units",
  "Billing category",
  "CRM",
  "Files by",
  "Model by",
  "Selected",
  "Model",
] as const;

const UNITS_MARKED_LABEL_TEXT: Record<string, string> = {
  "Design TN": "Design teeth",
  "Design units": "Design units",
  "Delivery TN": "Delivery teeth",
  "Delivery units": "Delivery units",
  "Billing category": "Billing",
  CRM: "CRM",
  "Files by": "Files by",
  "Model by": "Model by",
  Selected: "Model type",
  Model: "Model",
};

type UnitsMarkedRow = { label: string; value: string };

/** Turn the joined "Units marked. Label: value" sentence into labeled rows. */
const parseUnitsMarkedNote = (text: string): UnitsMarkedRow[] | null => {
  const raw = text.trim();
  if (!raw.startsWith("Units marked")) return null;

  const rest = raw.slice("Units marked".length).replace(/^\.\s*/, "");
  const labelPattern = new RegExp(
    `(?:^|\\.\\s+)(${UNITS_MARKED_LABELS.join("|")}):\\s*`,
    "g",
  );
  const matches = [...rest.matchAll(labelPattern)];
  const rows: UnitsMarkedRow[] = [];

  const introEnd = matches[0]?.index ?? rest.length;
  const intro = rest.slice(0, introEnd).replace(/\.\s*$/, "").trim();
  if (intro) {
    intro.split(/\.\s+/).forEach((part) => {
      const qc = part.match(/^QC units by\s+(.+)$/i);
      const qc2 = part.match(/^QC2 recorded as\s+(.+)$/i);
      const designer = part.match(/^Designer\s+(.+)$/i);
      if (qc) rows.push({ label: "QC", value: qc[1] });
      else if (qc2) rows.push({ label: "QC", value: qc2[1] });
      else if (designer) rows.push({ label: "Designer", value: designer[1] });
      else if (part.trim()) rows.push({ label: "Note", value: part.trim() });
    });
  }

  matches.forEach((match, index) => {
    const key = match[1];
    const valueStart = (match.index ?? 0) + match[0].length;
    const valueEnd = matches[index + 1]?.index ?? rest.length;
    const value = rest.slice(valueStart, valueEnd).replace(/\.\s*$/, "").trim();
    if (!value) return;
    rows.push({
      label: UNITS_MARKED_LABEL_TEXT[key] || key,
      value,
    });
  });

  return rows.length ? rows : null;
};

const UnitsMarkedNote = ({ rows }: { rows: UnitsMarkedRow[] }) => (
  <div className="mt-0.5 space-y-0.5 text-[11px] leading-snug">
    <p className="font-semibold text-gray-700">Units marked</p>
    <dl className="space-y-0.5">
      {rows.map((row, index) => (
        <div
          key={`${row.label}-${index}`}
          className="grid grid-cols-[6.1rem_minmax(0,1fr)] gap-x-2"
        >
          <dt className="text-gray-500">{row.label}</dt>
          <dd className="break-words text-gray-800">{row.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

/** Allocation / redesign rows written by the workflow, not typed by an employee. */
const isSystemPooledNote = (note: InternalNote) => {
  if ((note.source || "internal") === "internal") return false;

  const raw = String(note.note || "").trim();
  if (!raw || SYSTEM_NOTE_EXACT.has(raw)) return true;
  if (SYSTEM_NOTE_PREFIXES.some((prefix) => raw.startsWith(prefix))) return true;
  if (raw.includes("Reused existing allocation")) return true;
  if (raw.includes('Unhold from "')) return true;
  return false;
};

const InternalNotes = memo(() => {
  const notes = useAppSelector((state) => state.adminInternalNotes.notes);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Auto scroll when notes change (only if allowed)
  useEffect(() => {
    if (shouldAutoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [notes, shouldAutoScroll]);

  // Detect manual scroll
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;

    setShouldAutoScroll(isAtBottom);
  };

  if (notes.length === 0) {
    return <p className="text-gray-500">No notes yet.</p>;
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="space-y-1 overflow-y-auto pr-1 flex-1"
    >
      {notes.map((note, idx) => {
        const source = note.source || "internal";
        const author = note.author || note.noted_by || "Unknown";
        const when = note.creation_time || note.created_at;
        const displayNote =
          (note.note === "Case allocated" ||
            (note.note === "No note provided" &&
              !String(note.QC2 || "").trim())) &&
          note.allocator
            ? `Allocated by ${note.allocator}`
            : note.note;
        const humanWritten = !isSystemPooledNote(note);
        const unitsMarked = parseUnitsMarkedNote(String(displayNote || ""));
        return (
          <div
            key={idx}
            className={
              humanWritten
                ? "border border-blue-600 p-1 rounded-md bg-blue-600"
                : "border p-1 rounded-md bg-gray-50"
            }
          >
            <p
              className={
                humanWritten
                  ? "text-[10px] font-medium text-white uppercase tracking-wide"
                  : "text-[10px] font-medium text-gray-500 uppercase tracking-wide"
              }
            >
              {SOURCE_LABEL[source] || source}
            </p>
            {unitsMarked ? (
              <UnitsMarkedNote rows={unitsMarked} />
            ) : (
              <p className={humanWritten ? "text-white" : "text-gray-800"}>
                {displayNote}
              </p>
            )}
            <p
              className={
                humanWritten
                  ? "text-white text-[11px] mt-0"
                  : "text-gray-500 text-[11px] mt-0"
              }
            >
              — {author},{" "}
              <span className="italic">
                {when ? new Date(when).toLocaleString() : "Unknown date"}
              </span>
            </p>
          </div>
        );
      })}
    </div>
  );
});

export default InternalNotes;
